import { useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { detectClientPlatform } from '@/shared/lib/device';
import {
  appleWalletCardUrl,
  fetchGoogleWalletSaveUrl,
  type PublicInvitePassbook,
} from '../api/use-public-invite';
import { GoogleWalletBadge } from '@/shared/components/google-wallet-badge';
import { PublicInviteMobileHandoff } from './public-invite-mobile-handoff';
import { APPLE_WALLET_APP_URL, openGoogleWalletApp, resolveCardDelivery } from '../model/card-delivery';

interface PublicInviteSuccessProps {
  passbook: PublicInvitePassbook;
  serialNumber: string;
  /**
   * Cartão-presente novo (`omatuto`): no Android, o legado SEMPRE usa o link
   * de referral do Wallet Union quando não há `hasGWallet` — nunca cai no
   * `hasFlavor`/app genérico Fixe (`generateInviteGift`, routes/invite.js:1678-1706).
   */
  giftMode?: boolean;
}

/**
 * Entrega do cartão pós-cadastro. No legado, iOS baixa o `.pkpass` direto e
 * Android mostra o botão "Add to Google Wallet" — mas desktop cai (bug) no
 * mesmo fluxo Android quebrado. Aqui detectamos o device de verdade: iOS/Android
 * recebem o botão certo, desktop recebe a tela nova de handoff por QR.
 *
 * Lojas com app próprio (`passbook.hasFlavor`) recebem o link da loja de
 * apps em vez do cartão Wallet — replica a tabela de 13 marcas de
 * `legacy/fixe-link-2/routes/invite.js` (ver model/wallet-flavors.ts).
 */
export function PublicInviteSuccess({
  passbook,
  serialNumber,
  giftMode,
}: PublicInviteSuccessProps) {
  const { idPassbook } = passbook;
  const platform = detectClientPlatform();
  const [googleSaveUrl, setGoogleSaveUrl] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState(false);

  // Ordem do legado (Wallet Union > hasGWallet > hasFlavor > app genérico): ver model/card-delivery.ts.
  const delivery = resolveCardDelivery(passbook, platform, serialNumber, giftMode);
  const usesGoogleWallet = delivery.kind === 'google';

  useEffect(() => {
    if (!usesGoogleWallet) return;
    let cancelled = false;
    fetchGoogleWalletSaveUrl(idPassbook, serialNumber)
      .then((url) => {
        if (!cancelled) setGoogleSaveUrl(url);
      })
      .catch(() => {
        if (!cancelled) setGoogleError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [usesGoogleWallet, idPassbook, serialNumber]);

  if (delivery.kind === 'handoff') {
    const handoffUrl = `${window.location.origin}/invite/${idPassbook}?serial=${encodeURIComponent(serialNumber)}`;
    return <PublicInviteMobileHandoff handoffUrl={handoffUrl} />;
  }

  const appLink = delivery.kind === 'app' ? { href: delivery.href, label: delivery.label } : null;

  // Cinza neutro proposital (não a cor da loja nem o amarelo default) — pedido do
  // time pra ficar bonito em qualquer variação de cor de cliente, sem precisar
  // calibrar contraste caso a caso. Mesmo tom já usado no ícone inativo dos tabs.
  const neutralButtonClass = 'mt-6 w-full bg-foreground/70 text-white hover:bg-foreground/80';
  // "Já adicionou? Abrir Carteira/Google Wallet" — legacy/fixe-link-2
  // views/v2/partials/open_wallet_cta.ejs: sempre visível, sem delay/evento,
  // só no fluxo genérico (Apple/Google Wallet reais — não em appLink/flavor/Wallet Union).
  const openWalletLinkClass =
    'mt-3 block text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground';

  return (
    <Card className="mx-auto max-w-md p-8 text-center">
      <Wallet className="mx-auto size-12 text-foreground/70" />
      <h1 className="mt-4 text-xl font-semibold">Cadastro concluído!</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {appLink ? 'Agora é só baixar o app.' : 'Agora é só adicionar seu cartão à carteira.'}
      </p>

      {appLink ? (
        <Button asChild className={neutralButtonClass}>
          <a href={appLink.href}>{appLink.label}</a>
        </Button>
      ) : platform === 'ios' ? (
        <>
          <a
            href={appleWalletCardUrl(idPassbook, serialNumber)}
            className="mx-auto mt-6 block w-fit"
          >
            <img src="/applewallet.svg" alt="Adicionar ao Apple Wallet" className="h-11 w-auto" />
          </a>
          <a href={APPLE_WALLET_APP_URL} className={openWalletLinkClass}>
            Já adicionou? Abrir Carteira
          </a>
        </>
      ) : googleError ? (
        <p className="mt-6 text-sm text-destructive">
          Não foi possível gerar o cartão agora. Tente novamente em instantes.
        </p>
      ) : googleSaveUrl ? (
        <>
          <a href={googleSaveUrl} className="mx-auto mt-6 block w-fit" aria-label="Adicionar à Carteira do Google">
            <GoogleWalletBadge />
          </a>
          <button type="button" onClick={openGoogleWalletApp} className={openWalletLinkClass}>
            Já adicionou? Abrir Google Wallet
          </button>
        </>
      ) : (
        <Skeleton className="mx-auto mt-6 h-11 w-full rounded-lg" />
      )}
    </Card>
  );
}
